/**
 * Helpers de telefone/celular brasileiro.
 *
 * Regras (premissa do usuario):
 *   1. Em formulario: mascara aplicada em tempo de digitacao
 *      - Fixo 10 digitos: "(11) 1234-5678"
 *      - Movel 11 digitos: "(11) 91234-5678"
 *   2. Em banco: armazena SEM mascara, com "55" (Brasil) prefixado
 *      - Formato final: "5511912345678" (13 digitos para movel,
 *        12 digitos para fixo)
 *   3. Para exibicao: pode aplicar mascara novamente via formatPhoneBR
 *
 * Nao usa nenhuma biblioteca externa — logica simples o bastante
 * pra nao justificar react-input-mask, imask, etc.
 */

/**
 * Remove tudo que nao for digito. Se o numero comecar com "55" e tiver
 * 12-13 digitos, entende que ja tem DDI Brasil. Se tiver 10-11 digitos,
 * adiciona "55". Valores invalidos (<10 ou >13 digitos, ou com "55" mas
 * total errado) retornam null.
 *
 * Input "maskPhoneBR ready":
 *   "(11) 91234-5678"   -> "5511912345678"
 *   "(11) 1234-5678"    -> "551112345678"
 *   "+55 11 91234-5678" -> "5511912345678"
 *   "11912345678"       -> "5511912345678"
 *   "5511912345678"     -> "5511912345678"
 *   "912345678"         -> null  (faltam digitos)
 *   ""                  -> null
 */
export function normalizePhoneBR(input) {
  if (!input) return null;
  const digits = String(input).replace(/\D+/g, '');
  if (!digits) return null;

  // Ja tem DDI Brasil?
  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    return digits;
  }
  // 10 digitos (fixo com DDD) ou 11 digitos (movel com DDD) — adiciona 55
  if (digits.length === 10 || digits.length === 11) {
    return '55' + digits;
  }
  // Qualquer outro comprimento e invalido pra telefone brasileiro
  return null;
}

/**
 * Validacao estrita: retorna true apenas pra numeros que normalizePhoneBR
 * aceita. Use em schemas de validacao e guards de backend.
 */
export function isValidPhoneBR(input) {
  return normalizePhoneBR(input) !== null;
}

/**
 * Aplica mascara em tempo de digitacao. Tolerante a input parcial —
 * retorna o que conseguir formatar ate o momento.
 *
 *   "1"            -> "(1"
 *   "11"           -> "(11) "
 *   "1191"         -> "(11) 91"
 *   "11912345"     -> "(11) 91234-5"
 *   "11912345678"  -> "(11) 91234-5678"
 *   "1112345678"   -> "(11) 1234-5678"  (fixo 10 digitos)
 *   ""             -> ""
 *
 * Remove o DDI "55" se estiver presente (mascara brasileira e nacional,
 * nao internacional — o DDI e adicionado apenas em normalizePhoneBR).
 */
export function maskPhoneBR(input) {
  if (!input) return '';
  let digits = String(input).replace(/\D+/g, '');

  // Se tiver 55 + 10-11 digitos, remove o 55 pra mostrar so a parte BR
  if (digits.startsWith('55') && digits.length > 11) {
    digits = digits.slice(2);
  }

  // Limita ao maximo util (11 digitos — movel com DDD)
  digits = digits.slice(0, 11);

  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    // Fixo: (11) 1234-5678
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  // Movel: (11) 91234-5678
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

/**
 * Formata um numero ja armazenado (com "55") de volta em mascara BR
 * pra exibicao em listagens e cards.
 *
 *   "5511912345678" -> "(11) 91234-5678"
 *   "551112345678"  -> "(11) 1234-5678"
 *   "11912345678"   -> "(11) 91234-5678"
 *   null/""         -> ""
 */
export function formatPhoneBR(input) {
  if (!input) return '';
  return maskPhoneBR(input);
}

/**
 * Converte numero pro formato internacional E.164 com "+".
 * Util para links de WhatsApp (wa.me/...) e SMS links.
 *
 *   "5511912345678" -> "+5511912345678"
 *   "(11) 91234-5678" -> "+5511912345678"
 *   invalido -> ""
 */
export function toE164BR(input) {
  const n = normalizePhoneBR(input);
  return n ? `+${n}` : '';
}
